import { defineStore } from 'pinia';
import { saveStorage, getStorage } from '~/composables/UseStorage';

interface BrandingConfig {
    id?: string;
    entity_id?: number;
    logo_header_url?: string | null;
    logo_footer_url?: string | null;
    logo_background_enabled?: boolean;
    primary_color: string;
    secondary_color: string;
    footer_disclaimer?: string | null;
}

const defaultBranding: BrandingConfig = {
    primary_color: `rgb(102 41 214)`,
    secondary_color: '#FF874F',
    logo_background_enabled: false
};

export const useBrandingStore = defineStore('branding', {
    state: () => ({
        currentBranding: { ...defaultBranding } as BrandingConfig,
        isLoading: false,
        error: null as string | null,
    }),

    getters: {
        getBrandingConfig: (state) => state.currentBranding,
        getLogoHeaderUrl: (state) => state.currentBranding.logo_header_url || '/images/nla-logo.png',
        getLogoFooterUrl: (state) => state.currentBranding.logo_footer_url || '/images/nla-logo.png',
        getCompanyName: () => 'Novated Lease Australia', // Default company name
        getEntityId: () => {
            // Get the entity ID (selected organisation)
            // For dealer portal, branding is tied to DealerID (each dealer has their own branding)
            const authStore = useAuthStore();
            const {
                isDealerPortal
            } = usePortalTerminology();
            const selectedOrg = authStore.getSelectedOrganisation;

            return isDealerPortal.value ? selectedOrg?.DealerID : selectedOrg?.ClientID;
        }
    },

    actions: {
        async initializeBranding() {
            if (typeof window === 'undefined') return;

            // Try to load from storage first (organisation-specific)
            const authStore = useAuthStore();
            const selectedOrg = authStore.getSelectedOrganisation;
            if (selectedOrg?.ID) {
                const storageKey = `dealer-branding-${selectedOrg.ID}`;
                const stored = getStorage(storageKey);
                if (stored && stored.primary_color) {
                    this.currentBranding = {...defaultBranding, ...stored};
                    this.applyBrandingToDOM();
                }
            }

            // Then fetch from API to get latest
            await this.fetchBrandingConfig();
        },

        async fetchBrandingConfig() {
            this.isLoading = true;
            this.error = null;

            try {
                const config = useRuntimeConfig();

                const response = await fetch(`${config.public.apiBase}/api/dealer/branding?entity_id=${this.getEntityId}`, {
                    method: 'GET',
                    ...useAuthStore().UseAuthorization
                });

                if (response.ok) {
                    const data = await response.json();
                    this.currentBranding = { ...defaultBranding, ...data };

                    // Save to organisation-specific storage
                    const authStore = useAuthStore();
                    const selectedOrg = authStore.getSelectedOrganisation;
                    if (selectedOrg?.ID) {
                        const storageKey = `dealer-branding-${selectedOrg.ID}`;
                        saveStorage(storageKey, this.currentBranding);
                    }
                } else {
                    this.currentBranding = { ...defaultBranding };
                }

                this.applyBrandingToDOM();

                return this.currentBranding
            } catch (err) {
                console.warn('Error fetching branding config:', err);
                this.error = 'Failed to load branding configuration';
                this.currentBranding = { ...defaultBranding };
            } finally {
                this.isLoading = false;
            }
        },

        async updateBrandingConfig(newBranding: Partial<BrandingConfig>): Promise<boolean> {
            this.isLoading = true;
            this.error = null;

            try {
                const config = useRuntimeConfig();

                newBranding['entity_id'] = this.getEntityId

                const response = await fetch(`${config.public.apiBase}/api/dealer/branding`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...useAuthStore().UseAuthorization.headers
                    },
                    body: JSON.stringify(newBranding)
                });

                if (response.ok) {
                    const data = await response.json();
                    this.currentBranding = { ...defaultBranding, ...data };

                    // Save to organisation-specific storage
                    const authStore = useAuthStore();
                    const selectedOrg = authStore.getSelectedOrganisation;
                    if (selectedOrg?.ID) {
                        const storageKey = `dealer-branding-${selectedOrg.ID}`;
                        saveStorage(storageKey, this.currentBranding);
                    }

                    this.applyBrandingToDOM();
                    return true;
                } else {
                    this.error = `Failed to save branding: HTTP ${response.status}`;
                    return false;
                }
            } catch (err: any) {
                this.error = `Failed to save branding: ${err.message || 'Unknown error'}`;
                return false;
            } finally {
                this.isLoading = false;
            }
        },

        forceApplyBranding() {
            this.applyBrandingToDOM();
        },

        // Clear current branding state (useful when switching organisations)
        clearBrandingState() {
            this.currentBranding = { ...defaultBranding };
            this.isLoading = false;
            this.error = null;
            this.applyBrandingToDOM();
        },

        applyBrandingToDOM() {
            if (typeof window === 'undefined') return;

            const root = document.documentElement;

            // Apply CSS custom properties for dynamic theming
            root.style.setProperty('--branding-primary-color', this.currentBranding.primary_color);
            root.style.setProperty('--branding-secondary-color', this.currentBranding.secondary_color);

            // Override PrimeVue primary color variables with branding colors
            this.overridePrimeVueColors();
        },

        overridePrimeVueColors() {
            if (typeof window === 'undefined') return;

            const root = document.documentElement;
            const primaryColor = this.currentBranding.primary_color;

            // Convert hex to RGB for different shades
            const hexToRgb = (hex: string) => {
                const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                return result ? {
                    r: parseInt(result[1], 16),
                    g: parseInt(result[2], 16),
                    b: parseInt(result[3], 16)
                } : null;
            };

            const lighten = (color: string, amount: number) => {
                const rgb = hexToRgb(color);
                if (!rgb) return color;

                const r = Math.min(255, Math.floor(rgb.r + (255 - rgb.r) * amount));
                const g = Math.min(255, Math.floor(rgb.g + (255 - rgb.g) * amount));
                const b = Math.min(255, Math.floor(rgb.b + (255 - rgb.b) * amount));

                return `rgb(${r} ${g} ${b})`;
            };

            const darken = (color: string, amount: number) => {
                const rgb = hexToRgb(color);
                if (!rgb) return color;

                const r = Math.max(0, Math.floor(rgb.r * (1 - amount)));
                const g = Math.max(0, Math.floor(rgb.g * (1 - amount)));
                const b = Math.max(0, Math.floor(rgb.b * (1 - amount)));

                return `rgb(${r} ${g} ${b})`;
            };

            // Override PrimeVue primary color variables
            root.style.setProperty('--p-primary-50', lighten(primaryColor, 0.95));
            root.style.setProperty('--p-primary-100', lighten(primaryColor, 0.9));
            root.style.setProperty('--p-primary-200', lighten(primaryColor, 0.8));
            root.style.setProperty('--p-primary-300', lighten(primaryColor, 0.6));
            root.style.setProperty('--p-primary-400', lighten(primaryColor, 0.3));
            root.style.setProperty('--p-primary-500', primaryColor);
            root.style.setProperty('--p-primary-600', darken(primaryColor, 0.1));
            root.style.setProperty('--p-primary-700', darken(primaryColor, 0.2));
            root.style.setProperty('--p-primary-800', darken(primaryColor, 0.3));
            root.style.setProperty('--p-primary-900', darken(primaryColor, 0.4));
            root.style.setProperty('--p-primary-950', darken(primaryColor, 0.5));

            // Override derived colors
            root.style.setProperty('--p-primary-color', primaryColor);
            root.style.setProperty('--p-primary-hover-color', darken(primaryColor, 0.1));
            root.style.setProperty('--p-primary-active-color', darken(primaryColor, 0.2));
        }
    }
});
